import { useId } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Field, FieldError, FieldLabel } from "@volca/ui/components/ui/field";
import { Input } from "@volca/ui/components/ui/input";
import { Label } from "@volca/ui/components/ui/label";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { textVariants } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import type {
  ApQboBillPayload,
  ApQboBillVendor,
} from "@/accounts-payable/types";
import { QuickbooksExpenseAccountSelect } from "@/accounts-payable/vendors-tab/quickbooks-expense-account-select";
import { QuickbooksVendorDialog } from "@/accounts-payable/vendors-tab/quickbooks-vendor-dialog";
import type { QuickbooksVendor } from "@/business/quickbooks";
import { useGetQuickbooksAccounts } from "@/business/quickbooks-query";
import { quickbooksAccountLabel } from "@/business/quickbooks-utils";
import { SectionLabel } from "./shared";

/**
 * The edit form for a proposed QuickBooks bill: which vendor it goes to (an
 * existing QuickBooks vendor, or a new one by name) and its expense account.
 * Everything else on the bill comes from the invoice, so it isn't editable.
 */
export const QboBillEditor = ({
  businessId,
  draft,
  setDraft,
}: {
  businessId: string | null;
  draft: ApQboBillPayload;
  setDraft: (next: ApQboBillPayload) => void;
}) => {
  const accountFieldId = useId();
  const { data: accounts } = useGetQuickbooksAccounts(businessId);
  const { vendor } = draft;
  const isLink = vendor.mode === "link";
  const missingName = !isLink && !vendor.display_name.trim();

  const setVendor = (next: ApQboBillVendor) =>
    setDraft({ ...draft, vendor: next });
  const pickVendor = (picked: QuickbooksVendor, done: () => void) => {
    setVendor({
      mode: "link",
      quickbooks_vendor_id: picked.id,
      display_name: picked.display_name,
    });
    done();
  };
  const setAccount = (accountId: string | null) => {
    const account = accounts?.find((option) => option.id === accountId);
    if (!account) return;
    setDraft({
      ...draft,
      expense_account_id: account.id,
      expense_account_name: quickbooksAccountLabel(account),
    });
  };

  const vendorDialog = (
    <QuickbooksVendorDialog
      businessId={businessId}
      currentVendorId={isLink ? vendor.quickbooks_vendor_id : null}
      triggerLabel={isLink ? "Change" : "Choose an existing vendor"}
      triggerAriaLabel={
        isLink
          ? "Change the QuickBooks vendor for this bill"
          : "Choose an existing QuickBooks vendor for this bill"
      }
      title="Choose the QuickBooks vendor for this bill"
      description="Search your QuickBooks vendors for the one this bill goes to"
      confirmLabel="Use vendor"
      onLink={pickVendor}
    />
  );

  return (
    <Stack className="w-full" align="start" gap={4}>
      <Stack className="w-full" align="start" gap={2}>
        <SectionLabel>QuickBooks vendor</SectionLabel>
        {isLink ? (
          <Stack direction="row" gap={4} align="center" className="flex-wrap">
            <Text>{vendor.display_name}</Text>
            {vendorDialog}
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setVendor({ mode: "create", display_name: vendor.display_name })
              }
            >
              Create a new vendor instead
            </Button>
          </Stack>
        ) : (
          <Stack className="w-full" align="start" gap={2}>
            <Field data-invalid={missingName || undefined}>
              <FieldLabel htmlFor="new-vendor-name">New vendor name</FieldLabel>
              <Input
                id="new-vendor-name"
                aria-label="New vendor name"
                value={vendor.display_name}
                aria-invalid={missingName || undefined}
                onChange={(e) =>
                  setVendor({ mode: "create", display_name: e.target.value })
                }
              />
              {missingName && (
                <FieldError>Enter a name for the new vendor.</FieldError>
              )}
            </Field>
            {vendorDialog}
          </Stack>
        )}
      </Stack>

      <Stack className="w-full" align="start" gap={2}>
        <Label
          htmlFor={accountFieldId}
          className={cn(textVariants({ variant: "label" }), "text-subtle")}
        >
          Expense account
        </Label>
        <QuickbooksExpenseAccountSelect
          id={accountFieldId}
          businessId={businessId}
          allowDefault={false}
          value={{
            id: draft.expense_account_id,
            name: draft.expense_account_name,
            fully_qualified_name: null,
            account_type: null,
            account_sub_type: null,
            acct_num: null,
            // Only the select's label: the API checks the account on save.
            active: true,
          }}
          onChange={setAccount}
        />
      </Stack>

      <Text variant="caption" tone="muted">
        The amounts, dates and bill number come from the invoice.
      </Text>
    </Stack>
  );
};
